/** Zod schemas for all API request bodies (SG6: Input validation at all system boundaries). */
import { z } from "zod";
// POST /capture
export const LeadInputSchema = z.object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    company: z.string().optional(),
    website: z.string().optional(),
    sourceUrl: z.string().optional(),
    // NON-AUTHORITATIVE: the client's own observation of when it saw the lead. It travels to
    // the CRM as leadflow_captured_at and nothing else. It must NEVER reach captures.captured_at
    // — that column is what every quota window and the velocity abuse block count over, so a
    // client-chosen value there is a complete paywall bypass (see routes/capture.ts).
    capturedAt: z.string().optional(),
}).passthrough();
export const CaptureRequestSchema = z.object({
    lead: LeadInputSchema,
    provider: z.string().optional(),
    teamId: z.string().optional(),
});
// POST /billing/checkout
export const CheckoutRequestSchema = z.object({
    planId: z.enum(["starter", "pro", "team"]).optional(),
    successUrl: z.string().optional(),
    cancelUrl: z.string().optional(),
    affiliateCode: z.string().optional(),
    locale: z.string().optional(),
    currency: z.string().optional(),
    // zod v4 enforces safe integers here (|n| <= 2^53-1) — intentional tightening vs v3, pinned in schemas.test.ts.
    quantity: z.number().int().positive().optional(),
    interval: z.enum(["month", "year"]).optional(),
});
// POST /billing/cancel-reason
export const CancelReasonRequestSchema = z.object({
    reason: z.enum(["too_expensive", "no_longer_needed", "missing_feature", "technical_issue"]),
    detail: z.string().max(2000).optional(),
});
// POST /billing/portal
export const PortalRequestSchema = z.object({
    locale: z.string().optional(),
});
// POST /team (create team)
export const CreateTeamRequestSchema = z.object({
    name: z.string().min(1).max(100),
});
// POST /team/invite
export const TeamInviteRequestSchema = z.object({
    email: z.string().email(),
});
// PATCH /me
export const UpdateProfileRequestSchema = z.object({
    preferred_locale: z.string().min(2).max(35).optional(),
    preferred_currency: z.string().regex(/^[A-Z]{3}$/).optional(),
    timezone: z.string().min(3).max(50).optional(),
    analytics_consent_accepted: z.boolean().optional(),
});
// DELETE /me
export const DeleteAccountRequestSchema = z.object({
    confirm: z.literal(true),
});
// POST /analytics/events
export const AnalyticsEventRequestSchema = z.object({
    event: z.string().min(1),
    properties: z.record(z.string(), z.unknown()).optional(),
});
// POST /reminders
export const CreateReminderRequestSchema = z.object({
    title: z.string().min(1).max(200),
    note: z.string().max(2000).optional().nullable(),
    remindAt: z.string().min(1),
    captureId: z.string().optional().nullable(),
});
// PATCH /reminders/:id
export const UpdateReminderRequestSchema = z.object({
    title: z.string().min(1).max(200).optional(),
    note: z.string().max(2000).optional().nullable(),
    remindAt: z.string().optional(),
    completed: z.boolean().optional(),
});
// POST /referrals/attach
export const AttachReferralRequestSchema = z.object({
    referralCode: z.string().min(1),
});
// POST /crm/connect/weclapp/configure
export const WeclappConfigureRequestSchema = z.object({
    apiToken: z.string().min(1),
    tenantDomain: z.string().min(1),
});
// POST /feedback
export const FeedbackRequestSchema = z.object({
    content: z.string().min(1).max(5000),
    category: z.enum(["bug", "ux", "feature", "pricing"]).optional(),
    source: z.enum(["cancel_reason", "support", "in_app", "referral_comment"]).optional(),
});
// POST /admin/affiliate/payout
export const AffiliatePayoutRequestSchema = z.object({
    affiliateUserId: z.string().min(1),
});
//# sourceMappingURL=schemas.js.map